import type {TicketCostBucket, TicketCostBucketKey} from "./ticket-cost-types";
import {formatTicketCostCurrency, formatTicketCostPercent} from "./ticket-cost-types";

export type CostCompositionBarProps = {
  buckets: TicketCostBucket[];
  total?: number;
  currency?: string;
  compact?: boolean;
};

const bucketClasses: Record<TicketCostBucketKey, string> = {
  time: "bg-[var(--ots-primary)]",
  freight: "bg-[var(--ots-accent)]",
  load: "bg-[var(--ots-caution)]",
  tax: "bg-[var(--ots-success)]",
  specific: "bg-[var(--ots-text-muted)]",
};

/** Compact cost mix visualization that remains useful without a charting dependency. */
export const CostCompositionBar = ({buckets, total, currency = "USD", compact = false}: CostCompositionBarProps) => {
  const positiveBuckets = buckets.filter((bucket) => bucket.amount > 0);
  const resolvedTotal = total ?? positiveBuckets.reduce((sum, bucket) => sum + bucket.amount, 0);

  if (resolvedTotal <= 0 || positiveBuckets.length === 0) {
    return <p className="text-xs text-[var(--ots-text-muted)]">No cost composition is available.</p>;
  }

  return (
    <figure aria-label="Cost composition">
      <div className={["flex w-full overflow-hidden rounded-full bg-[var(--ots-surface-variant)]", compact ? "h-1.5" : "h-2.5"].join(" ")}>
        {positiveBuckets.map((bucket) => {
          const percent = bucket.amount / resolvedTotal;
          return (
            <span
              key={bucket.key}
              className={bucketClasses[bucket.key]}
              style={{width: `${Math.max(percent * 100, 0.75)}%`}}
              title={`${bucket.label}: ${formatTicketCostCurrency(bucket.amount, currency)} (${formatTicketCostPercent(percent)})`}/>
          );
        })}
      </div>
      <figcaption className={["flex flex-wrap items-center", compact ? "mt-2 gap-x-3 gap-y-1" : "mt-3 gap-x-5 gap-y-2"].join(" ")}>
        {positiveBuckets.map((bucket) => {
          const percent = bucket.amount / resolvedTotal;
          return (
            <span key={bucket.key} className="inline-flex items-center gap-1.5 text-xs text-[var(--ots-text-muted)]">
              <span aria-hidden="true" className={["size-2 rounded-full", bucketClasses[bucket.key]].join(" ")}/>
              <span>{bucket.label}</span>
              <strong className="font-semibold tabular-nums text-[var(--ots-text)]">{formatTicketCostCurrency(bucket.amount, currency)}</strong>
              {!compact && <span>· {formatTicketCostPercent(percent)}</span>}
            </span>
          );
        })}
      </figcaption>
    </figure>
  );
};

